// localStorage quota guard. Called once on boot from main.jsx.
//
// Snapshots, my-events, and votes all live in localStorage with no per-key
// expiry. Quota is 5–10MB depending on the browser. Heavy users will hit
// the cap over time; rather than letting setItem throw silently mid-vote,
// we proactively trim the oldest snapshots when usage crosses 80%.

const SNAPSHOT_PREFIX = 'verdant_snapshots_'
const RECENT_SNAPSHOTS_KEEP = 10
const QUOTA_THRESHOLD = 0.8

export async function checkAndPruneIfNeeded() {
  try {
    const ratio = await usageRatio()
    if (ratio !== null && ratio >= QUOTA_THRESHOLD) {
      pruneOldestSnapshots()
    }
  } catch { /* never block app boot on a quota probe */ }
}

async function usageRatio() {
  if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
    const { usage = 0, quota = 0 } = await navigator.storage.estimate()
    if (quota > 0) return usage / quota
  }
  // Fallback: rough byte count of localStorage against a conservative 5MB.
  return measureLocalStorageBytes() / (5 * 1024 * 1024)
}

function measureLocalStorageBytes() {
  if (typeof localStorage === 'undefined') return 0
  let total = 0
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i)
    if (!k) continue
    const v = localStorage.getItem(k) || ''
    // Approx byte cost: 2 per UTF-16 code unit for both key and value.
    total += (k.length + v.length) * 2
  }
  return total
}

// Trim every verdant_snapshots_<pollId> array down to the most recent
// RECENT_SNAPSHOTS_KEEP entries. Snapshots are ordered oldest-first so
// the head of each array is what we drop.
function pruneOldestSnapshots() {
  if (typeof localStorage === 'undefined') return
  const keys = []
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i)
    if (k && k.startsWith(SNAPSHOT_PREFIX)) keys.push(k)
  }
  let trimmed = 0
  for (const k of keys) {
    try {
      const raw = localStorage.getItem(k)
      if (!raw) continue
      const arr = JSON.parse(raw)
      if (!Array.isArray(arr) || arr.length <= RECENT_SNAPSHOTS_KEEP) continue
      const next = arr.slice(-RECENT_SNAPSHOTS_KEEP)
      localStorage.setItem(k, JSON.stringify(next))
      trimmed += arr.length - next.length
    } catch { /* skip malformed entries */ }
  }
  if (trimmed > 0) {
    console.info(`[Verdant] localStorage quota: pruned ${trimmed} snapshot entries`)
  }
}
